import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatDatepicker} from '@angular/material/datepicker';
import {Subject, takeUntil} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {CalendarDialogComponent} from '../../components/calendar-dialog.component';
import {JournalisationEvent} from '../../core/models/journalisation.model';
import {JournalisationService} from '../../core/services/journalisation.service';
import {FilterParams} from '../../core/models/habilitation-history.model';

@Component({
  selector: 'app-journalisation',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgClass
],
  templateUrl: './journalisation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './journalisation.component.scss'
})
export class JournalisationComponent implements OnInit, OnDestroy {

  @ViewChild(`calendarPicker`) calendarPicker!: MatDatepicker<Date>;
  private destroy$ = new Subject<void>();

  events: JournalisationEvent[] = [];
  filteredEvents: JournalisationEvent[] = [];
  paginatedEvents: JournalisationEvent[] = [];
  error: string | null = null;

  usersList: any[] = [];
  eventTypesList: string[] = [];

  totalEvents = 0;
  pageSize = 10;
  currentPage = 0;

  todayEventsCount = 0;
  pagination: any = null;

  selectedPeriod = '';
  selectedDate: Date | null = null;
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;

  selectedEventType = '';
  selectedUser = '';
  isLoading = false;
  currentFilters: FilterParams | undefined;

  // Propriétés pour la pagination personnalisée
  totalPages = 0;
  displayPages: (number | string)[] = [];

  constructor(
    private journalisationService: JournalisationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const cachedEvents = localStorage.getItem('journalisationEvents');
    if (cachedEvents) {
      this.events = JSON.parse(cachedEvents);
      this.extractUsersAndEventTypes();
      this.applyLocalFilters();
      this.isLoading = false;
    } else {
      this.loadLogings();
      this.subscribeToServiceState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.journalisationService.resetState();
  }

  loadLogings(): void {
    this.isLoading = true;
    this.journalisationService.getAllLogings(this.currentFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.events = (response.content as JournalisationEvent[]).map(event => {
            const eventDate = new Date(event.timestamp);
            return {
              ...event,
              dateString: eventDate.toLocaleDateString(),
              timeString: eventDate.toLocaleTimeString(),
              eventClass: this.getEventClass(event.eventType)
            } as JournalisationEvent;
          });

          localStorage.setItem('journalisationEvents', JSON.stringify(this.events));

          this.extractUsersAndEventTypes();
          this.applyLocalFilters();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des historiques:', error);
          this.error = 'Erreur lors du chargement des historiques';
          this.isLoading = false;
        }
      });
  }

  private getEventClass(eventType: string): string {
    if (eventType.includes('LOGIN') || eventType.includes('SUCCESS')) {
      return 'CREATE';
    } else if (eventType.includes('LOGOUT') || eventType.includes('ERROR')) {
      return 'DELETE';
    }
    return 'UPDATE';
  }

  getEventTypeIconColor(eventType: string): string {
    if (eventType.includes('LOGIN') || eventType.includes('SUCCESS')) {
      return 'green';
    } else if (eventType.includes('LOGOUT') || eventType.includes('ERROR')) {
      return 'red';
    }
    return 'blue';
  }

  onPeriodChange(): void {
    const now = new Date();

    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.selectedDate = null;

    switch (this.selectedPeriod) {
      case 'today':
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);
        this.selectedStartDate = todayStart;
        this.selectedEndDate = todayEnd;
        break;

      case 'week':
        const weekStart = new Date(now);
        const dayOfWeek = weekStart.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        weekStart.setDate(weekStart.getDate() - daysFromMonday);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        this.selectedStartDate = weekStart;
        this.selectedEndDate = weekEnd;
        break;

      case 'month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        monthStart.setHours(0, 0, 0, 0);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        this.selectedStartDate = monthStart;
        this.selectedEndDate = monthEnd;
        break;

      case 'calendar':
        this.openCalendarDialog();
        return;

      default:
        this.selectedStartDate = null;
        this.selectedEndDate = null;
        this.selectedDate = null;
        break;
    }

    this.applyDateFilter();
  }

  openCalendarDialog(): void {
    const dialogRef = this.dialog.open(CalendarDialogComponent, {
      width: '360px',
      panelClass: 'calendar-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((result: Date | null) => {
      if (result) {
        // Réinitialiser les plages de dates
        this.selectedStartDate = null;
        this.selectedEndDate = null;

        // Définir la date unique
        this.selectedDate = new Date(result);
        this.selectedDate.setHours(0, 0, 0, 0);

        this.applyDateFilter();
      } else {
        // L'utilisateur a fermé sans sélectionner
        this.selectedPeriod = '';
        this.selectedDate = null;
        this.selectedStartDate = null;
        this.selectedEndDate = null;
        this.applyDateFilter();
      }
    });
  }

  private applyDateFilter(): void {
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  private isEventOnSelectedDate(eventTimestamp: number): boolean {
    // Convertir les secondes en millisecondes
    const eventDate = new Date(eventTimestamp * 1000);
    eventDate.setHours(0, 0, 0, 0);

    // Si aucune période sélectionnée, accepter tous les événements
    if (!this.selectedStartDate && !this.selectedEndDate && !this.selectedDate) {
      return true;
    }

    // Cas 1 : Plage de dates (today, week, month)
    if (this.selectedStartDate && this.selectedEndDate) {
      const startDate = new Date(this.selectedStartDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(this.selectedEndDate);
      endDate.setHours(23, 59, 59, 999);

      return eventDate >= startDate && eventDate <= endDate;
    }

    // Cas 2 : Date unique (calendar)
    if (this.selectedDate) {
      const selectedDay = new Date(this.selectedDate);
      selectedDay.setHours(0, 0, 0, 0);

      const match = eventDate.getTime() === selectedDay.getTime();
      // console.log('Match:', match);
      return match;
    }

    return true;
  }

  private subscribeToServiceState(): void {
    this.journalisationService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(events => {
        this.events = events;
        this.extractUsersAndEventTypes();
        this.applyLocalFilters();
      });

    this.journalisationService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => this.isLoading = loading);

    this.journalisationService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => this.error = error);

    this.journalisationService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => this.pagination = pagination);
  }

  private extractUsersAndEventTypes(): void {
    const usersMap = new Map();
    const eventTypesSet = new Set<string>();

    this.events.forEach(event => {
      if (event.user) {
        if (!usersMap.has(event.user.id)) {
          usersMap.set(event.user.id, {
            id: event.user.id,
            firstName: event.user.firstName,
            lastName: event.user.lastName,
            email: event.user.email,
            username: event.user.username
          });
        }
      }

      if (event.eventType) {
        eventTypesSet.add(event.eventType);
      }
    });

    this.usersList = Array.from(usersMap.values())
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

    this.eventTypesList = Array.from(eventTypesSet).sort();
  }

  filterByEventType(event: any): void {
    this.selectedEventType = event.target.value;
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  filterByUser(event: any): void {
    this.selectedUser = event.target.value;
    this.currentPage = 0;
    this.applyLocalFilters();
  }

  private applyLocalFilters(): void {

    this.filteredEvents = this.events.filter(event => {
      const eventTypeMatch = !this.selectedEventType || event.eventType === this.selectedEventType;
      const userMatch = !this.selectedUser || event.user?.id === this.selectedUser;
      const dateMatch = this.isEventOnSelectedDate(event.timestamp);

      return eventTypeMatch && userMatch && dateMatch;
    });

    this.totalEvents = this.filteredEvents.length;

    // AJOUTEZ CETTE LIGNE (calculer totalPages)
    this.calculateTotalPages();

    // AJOUTEZ CETTE LIGNE (mettre à jour displayPages)
    this.updateDisplayPages();
    this.calculateTodayEvents();
    this.updatePaginatedEvents();
  }

  private calculateTodayEvents(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    this.todayEventsCount = this.events.filter(e => {
      // Convertir les secondes en millisecondes
      const eventDate = new Date(e.timestamp * 1000);

      return eventDate >= today && eventDate < tomorrow;
    }).length;
  }

  private updatePaginatedEvents(): void {
    // Réinitialiser currentPage s'il est hors limites
    if (this.currentPage * this.pageSize >= this.filteredEvents.length) {
      this.currentPage = 0;
    }

    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedEvents = this.filteredEvents.slice(startIndex, endIndex);
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedEvents();
  }

  getEventIcon(eventType: string): string {
    const icons: Record<string, string> = {
      'LOGIN': 'login',
      'LOGOUT': 'logout',
      'LOGIN_ERROR': 'error',
      'REGISTER': 'person_add',
      'UPDATE_PASSWORD': 'lock_reset',
      'UPDATE_PROFILE': 'edit',
      'VERIFY_EMAIL': 'mark_email_read'
    };

    for (const key in icons) {
      if (eventType.includes(key)) {
        return icons[key];
      }
    }
    return 'event';
  }

  getEventLabel(eventType: string): string {
    return eventType.replace(/_/g, ' ');
  }

  /**
   * Formate un timestamp en date et heure complète sur une ligne
   * @param timestamp - Timestamp en millisecondes ou secondes
   * @returns Chaîne formatée sous la forme "JJ/MM/AAAA HH:MM:SS"
   */
  formatDateTime(timestamp: number): string {
    if (!timestamp) {
      return 'N/A';
    }

    // Si le timestamp est en secondes (inférieur à 1e12), on le convertit en millisecondes
    const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);

    // Vérification que la date est valide
    if (isNaN(date.getTime())) {
      return 'Date invalide';
    }

    // Formatage de la date (JJ/MM/AAAA)
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    // Formatage de l'heure (HH:MM:SS)
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${day}/${month}/${year}  à  ${hours}h  ${minutes}mn ${seconds}s`;
  }


  // Méthodes pour la pagination personnalisée
  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
      this.currentPage = page - 1;
      this.updatePaginatedEvents();
      this.updateDisplayPages();
    }
  }

  private updateDisplayPages(): void {
    const pages: (number | string)[] = [];
    const currentPageNumber = this.currentPage + 1;

    if (this.totalPages <= 7) {
      // Afficher toutes les pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPageNumber <= 4) {
        // Début: 1 2 3 4 5 ... dernière
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages);
      } else if (currentPageNumber >= this.totalPages - 3) {
        // Fin: 1 ... avant-dernières ... dernière
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) pages.push(i);
      } else {
        // Milieu: 1 ... avant courant courant+1 après ... dernière
        pages.push(1);
        pages.push('...');
        pages.push(currentPageNumber - 1);
        pages.push(currentPageNumber);
        pages.push(currentPageNumber + 1);
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    this.displayPages = pages;
  }

  // Obtenir les pages à afficher avec élipsis
  getDisplayPages(): any [] {
    const pages: (number | string)[] = [];

    if (this.totalPages <= 7) {
      // Afficher toutes les pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const current = this.currentPage + 1;

      if (current <= 4) {
        // Début: 1 2 3 4 5 ... dernière
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages);
      } else if (current >= this.totalPages - 3) {
        // Fin: 1 ... avant-dernières ... dernière
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) pages.push(i);
      } else {
        // Milieu: 1 ... avant courant courant+1 après ... dernière
        pages.push(1);
        pages.push('...');
        pages.push(current - 1);
        pages.push(current);
        pages.push(current + 1);
        pages.push('...');
        pages.push(this.totalPages);
      }
    }

    return pages;
  }

  private calculateTotalPages(): void {
    if (this.filteredEvents && this.filteredEvents.length > 0 && this.pageSize > 0) {
      this.totalPages = Math.ceil(this.filteredEvents.length / this.pageSize);
    } else {
      this.totalPages = 0;
    }
  }

}
